import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOURNEY_LABELS = {
  "intro-call": "Begin Here",
  "career-coaching": "Flexible Support",
  "resume-repositioning": "Personal Brand",
  "interview-strategy": "Interview Preparation",
};

const JOURNEY_ORDER = ["intro-call", "career-coaching", "resume-repositioning", "interview-strategy"];

export default function ServicesPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then(res => setServices(res.data)).catch(() => {});
  }, []);

  const main = services.find(s => s.id === "repositioning-intensive");
  const others = JOURNEY_ORDER
    .map(id => services.find(s => s.id === id))
    .filter(Boolean);

  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <section className="bg-navy py-24 px-6 lg:px-12 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4">
          Advisory Services
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="font-lora text-4xl lg:text-5xl text-ivory font-normal">
          Where Would You Like to Begin?
        </motion.h1>
        <div className="w-12 h-px bg-gold mx-auto mt-6 mb-6"></div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }} className="font-serif text-ivory/50 text-sm max-w-xl mx-auto leading-relaxed">
          Every engagement is designed to move you forward — whether you are starting a conversation or committing to a complete repositioning.
        </motion.p>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">

        {/* Core Service — Repositioning Intensive */}
        {main && (
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="bg-white border-t-4 border-gold shadow-xl p-10 lg:p-14 mb-6 relative"
            data-testid="main-service-card"
          >
            <span className="absolute top-6 right-6 bg-gold text-navy text-xs tracking-widest uppercase px-3 py-1">Most Comprehensive</span>
            {main.sale_label && (
              <span className="absolute top-[52px] right-6 border border-[#C2B8A3] text-[#C2B8A3] font-serif text-[10px] tracking-[0.18em] uppercase px-2.5 py-0.5">{main.sale_label}</span>
            )}
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <p className="font-lora text-xs tracking-[0.2em] uppercase text-gold mb-3">Complete Career Repositioning</p>
                <h2 className="font-lora text-3xl text-navy font-normal mb-2">{main.name}</h2>
                <p className="font-lora text-3xl text-navy mb-2">${main.price} <span className="text-base font-normal text-navy/50">{main.currency}</span></p>
                <p className="text-navy/50 text-sm mb-6 tracking-wide">{main.duration}</p>
                <p className="text-navy/70 leading-relaxed font-serif mb-8">{main.description}</p>
                <Link to={`/booking?service=${main.id}`} className="inline-flex items-center gap-2 bg-navy text-ivory px-8 py-3 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all group" data-testid="main-service-book-btn">
                  Book This Service <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <div>
                {main.includes?.length > 0 && (
                  <>
                    <p className="font-lora text-xs tracking-[0.2em] uppercase text-navy/40 mb-4">Includes</p>
                    <ul className="space-y-3 mb-6">
                      {main.includes.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-navy/70 text-sm font-serif">
                          <CheckCircle size={15} className="text-gold mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <div className="border-l-2 border-gold pl-4 mt-6">
                  <p className="font-lora text-xs tracking-[0.2em] uppercase text-navy/40 mb-2">Outcome</p>
                  <p className="text-navy/60 text-sm font-serif italic leading-relaxed">{main.outcome}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Divider label */}
        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-soft-gray" />
          <p className="font-lora text-xs tracking-[0.25em] uppercase text-navy/30 whitespace-nowrap">Or begin with a focused session</p>
          <div className="flex-1 h-px bg-soft-gray" />
        </div>

        {/* Secondary Services — 2×2 grid */}
        <div className="grid sm:grid-cols-2 gap-6 mb-16">
          {others.map((svc, i) => (
            <motion.div
              key={svc.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`${svc.price === 0 ? "bg-[#F4F3EF] border-gold/40" : "bg-white border-soft-gray hover:border-gold/60"} border transition-all duration-300 p-8 flex flex-col`}
              data-testid={`service-${svc.id}`}
            >
              {/* Journey label */}
              <p className={`font-lora text-xs tracking-[0.22em] uppercase mb-2 ${svc.price === 0 ? "text-gold" : "text-navy/35"}`}>
                {JOURNEY_LABELS[svc.id] || ""}
              </p>

              {/* Name + price */}
              <h3 className="font-lora text-xl text-navy font-normal mb-1">{svc.name}</h3>
              {svc.price === 0 ? (
                <p className="font-lora text-gold text-2xl mb-1">Complimentary</p>
              ) : (
                <p className="font-lora text-gold text-2xl mb-1">${svc.price}</p>
              )}
              <p className="text-navy/40 text-xs mb-5 tracking-wide">{svc.currency} · {svc.duration}</p>

              {/* Description */}
              <p className="text-navy/65 text-sm font-serif leading-relaxed mb-6">{svc.description}</p>

              {/* Includes */}
              {svc.includes?.length > 0 && (
                <div className="mb-6">
                  <p className="font-lora text-[10px] tracking-[0.22em] uppercase text-navy/30 mb-3">Includes</p>
                  <ul className="space-y-2">
                    {svc.includes.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-navy/65 text-sm font-serif">
                        <CheckCircle size={13} className="text-gold mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Outcome */}
              {svc.outcome && (
                <div className="border-l-2 border-gold/50 pl-3 mb-6 mt-auto">
                  <p className="text-navy/50 text-xs font-serif italic leading-relaxed">{svc.outcome}</p>
                </div>
              )}

              {/* CTA */}
              <Link
                to={`/booking?service=${svc.id}`}
                className="inline-flex items-center gap-2 text-navy text-xs tracking-widest uppercase hover:text-gold transition-colors group mt-auto"
                data-testid={`book-${svc.id}-btn`}
              >
                {svc.price === 0 ? "Book Complimentary Call" : "Book This Session"}
                <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Guidance quote */}
        <div className="text-center py-10 border-t border-soft-gray mb-10">
          <p className="text-navy/50 font-serif italic max-w-2xl mx-auto leading-relaxed text-sm">
            "Not sure where to start? The complimentary call is designed to help you find out — and most clients find that one conversation is all it takes to gain clarity on the right next step."
          </p>
        </div>

        {/* Custom Packages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="bg-[#F4F3EF] border border-soft-gray p-8 lg:p-10"
          data-testid="custom-packages-section"
        >
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <p className="font-lora text-xs tracking-[0.2em] uppercase text-gold mb-2">Unique Situations</p>
              <h3 className="font-lora text-2xl text-navy font-normal mb-3">Custom Packages Available</h3>
              <p className="text-navy/65 font-serif text-sm leading-relaxed">
                Some individuals arrive with situations that do not fit neatly into a single service category. If your circumstances are layered, ongoing, or require a tailored approach — a custom package can be structured around your specific needs.
              </p>
              <p className="text-navy/50 font-serif text-sm leading-relaxed mt-3">
                Custom engagements may include extended advisory time, multiple sessions, combined deliverables, or a scope defined entirely around what is most useful to you.
              </p>
            </div>
            <div className="text-center lg:text-right">
              <p className="font-lora text-navy/50 italic text-base mb-5 font-normal">Priced based on scope and individual need.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3 text-xs tracking-[0.15em] uppercase hover:bg-navy/90 transition-all group">
                Enquire <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
